import {
  DEFAULT_BOOT_LOCKUP_ANIMATED_URL,
  DEFAULT_BOOT_LOCKUP_URL,
  DEFAULT_BOOT_SPLASH_FRAME_URL,
  PLAYER_BOOT_THEME_COLOR,
  resolveDefaultBootSplashIsAnimated,
  resolveDefaultBootSplashUrl
} from '@/constants/bootChrome'
import {
  readManifestHasCustomLogo,
  resolveBootPlayerBrandingUrls
} from '@/helpers/playerBrandingCache'

const BOOT_FRAME_BG = PLAYER_BOOT_THEME_COLOR

function applyBootSplashFrameBackground(el: HTMLElement, url: string): void {
  el.style.backgroundColor = BOOT_FRAME_BG
  el.style.backgroundImage = `url(${url})`
  el.style.backgroundSize = 'contain'
  el.style.backgroundPosition = 'center'
  el.style.backgroundRepeat = 'no-repeat'
}

/** Cached index.html (pre-#488) used boot-logo.png + loader ring; normalize to idle lockup chrome. */
function ensureBootLockupPresentation(
  loadingRoot: HTMLElement,
  img: HTMLImageElement
): HTMLImageElement {
  loadingRoot.querySelectorAll('.player-idle-loader-ring').forEach(node => node.remove())

  if (img.classList.contains('player-idle-chrome-logo') && img.parentElement?.classList.contains('player-boot-lockup-wrap')) {
    return img
  }

  const wrap = document.createElement('div')
  wrap.className = 'player-idle-chrome-logo-wrap player-boot-lockup-wrap'
  img.id = 'app-loading-splash'
  img.className = 'player-idle-chrome-logo'
  img.alt = 'Lucimark'
  img.removeAttribute('width')
  img.removeAttribute('height')

  const parent = img.parentElement ?? loadingRoot
  parent.insertBefore(wrap, img)
  wrap.appendChild(img)
  return img
}

function ensureBootSplashFramePresentation(loadingRoot: HTMLElement, el: HTMLElement): HTMLElement {
  loadingRoot.querySelectorAll('.player-idle-loader-ring').forEach(node => node.remove())

  if (el instanceof HTMLImageElement) {
    const div = document.createElement('div')
    div.id = 'app-loading-splash'
    div.className = 'player-boot-splash-frame'
    div.setAttribute('role', 'img')
    div.setAttribute('aria-label', 'Lucimark')
    el.replaceWith(div)
    return div
  }

  const wrap = el.parentElement
  if (wrap?.classList.contains('player-boot-lockup-wrap') || wrap?.classList.contains('player-boot-lockup-animated-wrap')) {
    wrap.replaceWith(el)
  }

  el.id = 'app-loading-splash'
  el.className = 'player-boot-splash-frame'
  el.setAttribute('role', 'img')
  el.setAttribute('aria-label', 'Lucimark')
  return el
}

function ensureBootAnimatedLockupPresentation(loadingRoot: HTMLElement, from: HTMLElement): HTMLElement {
  loadingRoot.querySelectorAll('.player-idle-loader-ring').forEach(node => node.remove())

  let wrap: HTMLElement
  let img: HTMLImageElement

  if (from.classList.contains('player-boot-lockup-animated-wrap')) {
    wrap = from
    const existing = wrap.querySelector('img.player-boot-lockup-animated')
    if (existing instanceof HTMLImageElement) {
      img = existing
    }
    else {
      img = document.createElement('img')
      img.className = 'player-boot-lockup-animated'
      img.alt = 'Lucimark'
      wrap.replaceChildren(img)
    }
  }
  else if (
    from instanceof HTMLImageElement
    && from.classList.contains('player-boot-lockup-animated')
  ) {
    wrap = document.createElement('div')
    wrap.className = 'player-boot-lockup-animated-wrap'
    wrap.setAttribute('role', 'img')
    wrap.setAttribute('aria-label', 'Lucimark')
    img = from
    from.replaceWith(wrap)
    wrap.appendChild(img)
  }
  else {
    wrap = document.createElement('div')
    wrap.className = 'player-boot-lockup-animated-wrap'
    wrap.setAttribute('role', 'img')
    wrap.setAttribute('aria-label', 'Lucimark')
    img = document.createElement('img')
    img.className = 'player-boot-lockup-animated'
    img.alt = 'Lucimark'
    from.replaceWith(wrap)
    wrap.appendChild(img)
  }

  wrap.id = 'app-loading-splash'
  wrap.className = 'player-boot-lockup-animated-wrap'
  img.className = 'player-boot-lockup-animated'
  img.alt = 'Lucimark'
  return wrap
}

/** @deprecated Legacy cached HTML — delegates to frame or lockup presentation. */
function upgradeLegacyBootShell(loadingRoot: HTMLElement): HTMLElement | null {
  let splash = document.getElementById('app-loading-splash')
  if (splash == null) {
    splash = loadingRoot.querySelector<HTMLElement>(
      '.player-boot-splash-frame, .player-boot-lockup-animated-wrap, .player-idle-chrome-logo--boot, .player-boot-splash, #app-loading-splash'
    )
  }
  if (splash == null) {
    return null
  }
  if (loadingRoot.classList.contains('app-loading--tenant-logo')) {
    if (splash instanceof HTMLImageElement) {
      return ensureBootLockupPresentation(loadingRoot, splash)
    }
    const img = document.createElement('img')
    img.alt = 'Lucimark'
    splash.replaceWith(img)
    return ensureBootLockupPresentation(loadingRoot, img)
  }
  if (resolveDefaultBootSplashIsAnimated()) {
    return ensureBootAnimatedLockupPresentation(loadingRoot, splash)
  }
  return ensureBootSplashFramePresentation(loadingRoot, splash)
}

const loadingEl = document.getElementById('app-loading')
const splashEl
  = loadingEl != null
    ? upgradeLegacyBootShell(loadingEl)
    : document.getElementById('app-loading-splash')
const bgEl = document.getElementById('app-loading-bg')

// Re-run the inline window-safe rotation (index.html) after the shell was rebuilt.
;(window as unknown as { __lucimarkApplyBootRotation?: () => void }).__lucimarkApplyBootRotation?.()

function applyDefaultBootSplash(): void {
  if (splashEl == null || loadingEl == null) {
    return
  }
  const url = resolveDefaultBootSplashUrl()
  if (resolveDefaultBootSplashIsAnimated()) {
    const wrap = ensureBootAnimatedLockupPresentation(loadingEl, splashEl)
    const img = wrap.querySelector('img.player-boot-lockup-animated')
    if (img instanceof HTMLImageElement) {
      img.onerror = () => {
        if (img.dataset.fallbackBootFrame === '1') {
          return
        }
        img.dataset.fallbackBootFrame = '1'
        const frameEl = ensureBootSplashFramePresentation(loadingEl, wrap)
        applyBootSplashFrameBackground(frameEl, DEFAULT_BOOT_SPLASH_FRAME_URL)
      }
      img.src = url
    }
    return
  }
  const frameEl = ensureBootSplashFramePresentation(loadingEl, splashEl)
  const probe = new Image()
  probe.onload = () => {
    applyBootSplashFrameBackground(frameEl, url)
  }
  probe.onerror = () => {
    if (frameEl.dataset.fallbackBootLockup === '1') {
      return
    }
    frameEl.dataset.fallbackBootLockup = '1'
    const img = replaceSplashWithLockupImg(loadingEl, frameEl)
    img.src = DEFAULT_BOOT_LOCKUP_URL
  }
  probe.src = url
}

const pendingTenantLogo
  = loadingEl?.classList.contains('app-loading--pending-tenant-logo') === true
    || readManifestHasCustomLogo()

function hideBootSplashUntilBranding(): void {
  if (loadingEl == null) {
    return
  }
  loadingEl.classList.add('app-loading--pending-tenant-logo')
  const current = document.getElementById('app-loading-splash')
  current?.setAttribute('hidden', '')
}

if (splashEl != null && loadingEl != null && !loadingEl.classList.contains('app-loading--tenant-logo')) {
  if (pendingTenantLogo) {
    // Manifest already has a custom logo URN — keep charcoal only until the blob resolves.
    hideBootSplashUntilBranding()
  }
  else {
    applyDefaultBootSplash()
  }
}

function replaceSplashWithLockupImg(loadingRoot: HTMLElement, from: HTMLElement): HTMLImageElement {
  const img = document.createElement('img')
  img.id = 'app-loading-splash'
  img.alt = 'Lucimark'
  from.replaceWith(img)
  return ensureBootLockupPresentation(loadingRoot, img)
}

document.getElementById('app-loading-splash')?.addEventListener('error', (ev) => {
  const current = ev.target
  if (current == null || !(current instanceof HTMLImageElement) || current.dataset.fallbackBootLockup === '1') {
    return
  }
  if (loadingEl == null) {
    return
  }
  current.dataset.fallbackBootLockup = '1'
  if (current.dataset.tenantLogo === '1') {
    loadingEl.classList.remove('app-loading--tenant-logo')
    delete current.dataset.tenantLogo
    if (resolveDefaultBootSplashIsAnimated()) {
      const wrap = ensureBootAnimatedLockupPresentation(loadingEl, current)
      const img = wrap.querySelector('img.player-boot-lockup-animated')
      if (img instanceof HTMLImageElement) {
        img.src = DEFAULT_BOOT_LOCKUP_ANIMATED_URL
      }
      return
    }
    const frameEl = ensureBootSplashFramePresentation(loadingEl, current)
    applyBootSplashFrameBackground(frameEl, DEFAULT_BOOT_SPLASH_FRAME_URL)
    return
  }
  if (current.classList.contains('player-boot-lockup-animated')) {
    const wrap = current.closest('#app-loading-splash') ?? current
    const frameEl = ensureBootSplashFramePresentation(loadingEl, wrap instanceof HTMLElement ? wrap : current)
    applyBootSplashFrameBackground(frameEl, DEFAULT_BOOT_SPLASH_FRAME_URL)
    return
  }
  current.src = DEFAULT_BOOT_LOCKUP_URL
}, true)

async function applyBootBranding() {
  const defaultFrameUrl = resolveDefaultBootSplashUrl()
  const { logoUrl, backgroundUrl } = await resolveBootPlayerBrandingUrls(defaultFrameUrl)
  if (splashEl != null && loadingEl != null) {
    if (logoUrl.startsWith('blob:')) {
      loadingEl.classList.remove('app-loading--pending-tenant-logo')
      loadingEl.classList.add('app-loading--tenant-logo')
      const current = document.getElementById('app-loading-splash') ?? splashEl
      current.removeAttribute('hidden')
      const img = current instanceof HTMLImageElement
        ? ensureBootLockupPresentation(loadingEl, current)
        : replaceSplashWithLockupImg(loadingEl, current)
      img.removeAttribute('hidden')
      img.dataset.tenantLogo = '1'
      img.src = logoUrl
    }
    else if (loadingEl.classList.contains('app-loading--pending-tenant-logo')) {
      // Custom URN known but blob unavailable — fall back to Lucimark chrome.
      loadingEl.classList.remove('app-loading--pending-tenant-logo')
      const current = document.getElementById('app-loading-splash')
      current?.removeAttribute('hidden')
      applyDefaultBootSplash()
    }
  }
  if (backgroundUrl != null && bgEl != null) {
    bgEl.style.backgroundImage = `url(${backgroundUrl})`
    loadingEl?.classList.add('app-loading--custom-bg')
  }
}

void applyBootBranding()
